import { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { t } from '@lingui/macro';
import _ from 'lodash';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { i18n } from '@lingui/core';
import moment from 'moment-timezone';
import Panel from '../../page-framework/panel/PanelDecorator';
import { panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import {
    businessServiceFilterId,
    siteFilterId,
    statusFilterId,
    testTypeFilterId,
    timeRangeFilterId,
} from '../../filters';
import { useDataService } from '../../../services/ApiDataService';
import { dateTimeFormatter, formatNumber } from '../../../utilities/FormattingUtilities';
import { QUICK_TEST_STATUS } from '../quick-test-panel/QuickTestConstants';
import VoipTestNowResultsChartsAndMetrics from './VoipTestNowResultsChartsAndMetrics';
import useEnvironment from '../../../stores/EnvironmentStore';
import {
    VOIP_PULSE_TEST_TYPE,
    METRIC_UNITS,
    EMPTY,
    AVG_METRIC_LUT,
    STATUS_AGENT,
    METRIC_LUT,
    TEST_STATUS_LUT,
    MS,
    PHONE,
    STATUS_LUT,
} from './VoipTestConstants';
import { getChartOptions, getGaugeChartConfig, mosSquareColor } from './VoipPulseTestHelpers';
import useUiToasts, { createToastConfig, INFO } from '../../../stores/ToastStore';
import { getSystemTime } from '../../../stores/SystemTimeStore';
import { getLogger } from '../../../utilities/UseLog';
import useAlerts from '../../../stores/AlertsStore';
import { buildLinkUrl } from '../../links/Link';
import { shouldPoll } from '../quick-test-panel/QuickTestHelpers';

const log = getLogger('VoipTestNowTestResultPanel');

const title = t`VoIP Test Now Result`;

const POLLING_INTERVAL = 1000 * 1.5; // 1.5 seconds

const Header = ({ results }) => {
    if (!results?.test?.name) return null;

    return (
        <>
            <Box position="relative" display="flex" justifyContent="flex-end" alignItems="center" pt={1} px={2} pb={2}>
                <Box position="absolute" left="50%" style={{ transform: 'translateX(-50%)' }} textAlign="center">
                    <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                        {results?.status}
                        {results.timedOut === true && <span> - Timed Out</span>}
                        {results.clockRunning && <span> - {formatNumber(results.clock.val, 0)} seconds</span>}
                    </Typography>
                </Box>
                <Box textAlign="right">
                    <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                        {results?.createdAtFmt}
                    </Typography>
                </Box>
            </Box>
            {results.showLoading && (
                <Box display="flex" justifyContent="center" alignItems="center" height={28} width="100%">
                    <CircularProgress size={28} />
                </Box>
            )}
        </>
    );
};

const VoipTestNowTestResultPanel = forwardRef((props, ref) => {
    const quickTestUrl = `/ipm/v1/admin/quicktests/:_id`;
    const testTypeUrl = '/ipm/v1/open/testTypes';
    const theme = useTheme();
    const [environment] = useEnvironment();
    const dataService = useDataService();
    const [, /* Not used */ alertsActions] = useAlerts();
    const { setCancelTestDisabled, setReRunDisabled } = props;
    const [, uiToastActions] = useUiToasts();
    const [results, setResults] = useState({});
    const [isPolling, setIsPolling] = useState(true);
    const [callFlows, setCallFlows] = useState({});
    const timerRef = useRef(null);
    const mountedRef = useRef(true);
    const callerCalleeRef = useRef(['caller', 'callee']);
    const thresholdRef = useRef(null);
    const pollingIntervalRef = useRef(POLLING_INTERVAL);

    const mosColor = (value) => {
        const threshold = thresholdRef.current;
        if (!threshold) return '';
        return mosSquareColor({ fair: threshold.critical, good: threshold.degraded }, value);
    };

    useImperativeHandle(ref, () => ({
        getResults: () => results,
    }));

    const getQuickTestById = useCallback(
        async (id) => {
            const url = buildLinkUrl({ target: quickTestUrl }, { _id: id }, null);
            const response = await dataService.get(url, {
                query: JSON.stringify({
                    status: _.values(QUICK_TEST_STATUS),
                }),
            });
            return response;
        },
        [dataService, quickTestUrl]
    );
    /**
     * Find the SIP messages and put them on the screen.
     * @param test
     * @returns {*}
     */
    const buildCallFlow = useCallback((test) => {
        const flows = {};

        if (!test.agentResults) {
            return test;
        }

        _.each(test.agentResults, (agentResult) => {
            flows[callerOrCallee(test.testConfig, agentResult.agentGuid)] = _.get(
                getCallSummary(agentResult) || {},
                'data.signalling_collection',
                []
            );
        });

        setResults((prev) => ({ ...prev, flowGathered: true }));
        setCallFlows(flows);
        return test;
    }, []);

    /**
     * Figure out if we have a call summary or not.
     * @returns {boolean}
     */
    const hasSummary = useCallback((res) => {
        let summary = false;
        // parse the sip messages out of the call flow
        _.each(res.test?.agentResults, (agentResult) => {
            if (!_.isEmpty(getCallSummary(agentResult))) {
                summary = true;
            }
        });

        return summary;
    }, []);
    /**
     * Function is run when the test is complete / errored and not polling anymore
     */
    const testDone = useCallback(
        (test, res) => {
            setResults((prev) => ({ ...(prev || {}), testRunning: false }));
            setIsPolling(false);
            if (hasSummary(res)) {
                buildCallFlow(res.test || test);
                return Promise.resolve();
            }

            return new Promise((resolve) => {
                const waitTimer = setTimeout(() => {
                    if (!mountedRef.current) {
                        resolve();
                        return;
                    }
                    log.info('One last try to get the summary');
                    getQuickTestById(test._id).then((latest) => {
                        if (!mountedRef.current) return resolve();
                        buildCallFlow(latest);
                        resolve();
                    });
                }, 15000);

                timerRef.current = waitTimer;
            });
        },
        [getQuickTestById, buildCallFlow, hasSummary]
    );

    /**
     * update summary metrics of test metric panel
     * @returns {*}
     */
    const updateSummaryMetrics = useCallback((callSummary, ccKey, ccNPoint, status, proxySet) => {
        let srcIpUsed = callSummary.data.src_ip_used;
        const nPointMgtIP = ccNPoint.ipv4Local;
        srcIpUsed = srcIpUsed || nPointMgtIP || EMPTY;
        // when the test is complete we should use the call Summary result instead for avgs
        if (status === QUICK_TEST_STATUS.COMPLETE) {
            _.each(_.keys(AVG_METRIC_LUT), (avgKey) => {
                proxySet[ccKey].avgs[avgKey] = normalizeData(
                    AVG_METRIC_LUT[avgKey],
                    callSummary.data[AVG_METRIC_LUT[avgKey]]
                );
                proxySet[ccKey].avgsFmt[avgKey] = normalizeFmtData(
                    AVG_METRIC_LUT[avgKey],
                    callSummary.data[AVG_METRIC_LUT[avgKey]]
                );
            });

            // there are values in teh summary that we display on the table
            // MDL - Check for number or string as we appear to get a mixture...
            proxySet[ccKey].summary = {
                dialDelay:
                    callSummary.data.dial_delay === -1 || callSummary.data.dial_delay === '-1'
                        ? EMPTY
                        : callSummary.data.dial_delay + MS,
                ringDelay:
                    callSummary.data.ring_delay === -1 || callSummary.data.ring_delay === '-1'
                        ? EMPTY
                        : callSummary.data.ring_delay + MS,
                dataRate:
                    callSummary.data.throughput === -1 || callSummary.data.throughput === '-1'
                        ? EMPTY
                        : callSummary.data.throughput,
                codec: !callSummary.data.codec ? EMPTY : callSummary.data.codec,
                srcIpUsed,
            };
        } else {
            // get ip used for the test
            proxySet[ccKey].summary = {
                srcIpUsed,
            };
        }
    }, []);

    /**
     * updates the proxy storage, Proxy storage looks like
     * {
     *   caller: {
     *       updateTimes: [Date, ... , Date] // this is for determining what the data set has  when new resutls come in
     *       lqmos_curr: [Date, value],
     *       key2: [Date, value]
     *   },
     *   callee: {
     *      // same as above
     *   }
     * }
     * @param keys
     * @param ccKey
     * @param agentResult
     * @param proxySet
     * @returns {*}
     */
    const updateChartData = useCallback(
        (keys, ccKey, ccNPoint, agentResult, proxySet, status) => {
            // MDL - This used to _.chain the filter and for each, but lodash must not
            //  support that as it never got into the forEach loop.  Separating them out seems to have
            //  made it work.
            const callTimeSeries = _.filter(agentResult.results, (result) => result.resultType === 'callTimeSeries');

            _.each(callTimeSeries, (result) => {
                const parsedDateTime = Date.parse(result.timestamp);

                // does the data already exist in the set
                const exists = _.intersection(proxySet[ccKey].updateTimes, [parsedDateTime]).length > 0;

                if (!exists) {
                    // we need to add the times that are already in the proxy set
                    proxySet[ccKey].updateTimes.push(parsedDateTime);

                    // put the data into the proxy set
                    _.each(keys, (key) => {
                        const resultData = normalizeData(key, result.data[key]);
                        proxySet[ccKey][key].push([parsedDateTime, resultData]);
                    });

                    // test is still running
                    if (status !== QUICK_TEST_STATUS.COMPLETE) {
                        // update the averages
                        _.each(_.keys(AVG_METRIC_LUT), (avgKey) => {
                            // we only want to update if its the latest avg
                            if (parsedDateTime === _.max(proxySet[ccKey].updateTimes)) {
                                proxySet[ccKey].avgs[avgKey] = normalizeData(
                                    AVG_METRIC_LUT[avgKey],
                                    result.data[AVG_METRIC_LUT[avgKey]]
                                );
                                proxySet[ccKey].avgsFmt[avgKey] = normalizeFmtData(
                                    AVG_METRIC_LUT[avgKey],
                                    result.data[AVG_METRIC_LUT[avgKey]]
                                );
                            }
                        });
                    }
                }
            });

            const callSummary = getCallSummary(agentResult);
            if (!callSummary) {
                return proxySet;
            }

            updateSummaryMetrics(callSummary, ccKey, ccNPoint, status, proxySet);

            return proxySet;
        },
        [updateSummaryMetrics]
    );

    /**
     * the status bar that appears when the test is running
     * @param ccKey
     * @param test
     */
    const updateStatusBar = useCallback((ccKey, test, res) => {
        //  states are in order this is important do not change the order
        const STATES = ['O', 'Q', 'W', 'A', 'R', 'H', 'C'];
        const NUM_OF_VALID_STATES = STATES.length;
        const BAR_PCT_PER_STATE = 100 / NUM_OF_VALID_STATES;

        // update the progress bar and status
        const agent = getCallerOrCallee(test, ccKey);
        res.bar[ccKey].status = STATUS_AGENT[ccKey][agent.status];
        if (agent.status === 'P') agent.status = 'A';
        if (agent.status === 'T') agent.status = 'H';
        const progressIdx = _.indexOf(STATES, agent.status) + 1;
        res.bar[ccKey].val = progressIdx * BAR_PCT_PER_STATE;
    }, []);

    /**
     * munges the new data coming in and assign them to appropiate places
     * @param test
     * @returns {*}
     */
    const aggregateTestResults = useCallback(
        (threshold, test, res) => {
            res.test = test;
            res.status = TEST_STATUS_LUT[test.status];
            res.showLoading = shouldPoll(test.status);
            res.timedOut = QUICK_TEST_STATUS.ERROR === test.status && !_.isUndefined(test.forceCompletedAt);
            const callerCallee = callerCalleeRef.current;

            // edit the proxy set
            _.each(test.agentResults, (agentResult) => {
                const ccKey = callerOrCallee(res.test.testConfig, agentResult.agentGuid);
                const ccNPoint = res.test.testConfig[ccKey];
                res.config.results = updateChartData(
                    _.values(METRIC_LUT),
                    ccKey,
                    ccNPoint,
                    agentResult,
                    res.config.results,
                    test.status
                );
            });

            // update the charts and gages (Averages) and status bar
            _.each(callerCallee, (ccKey) => {
                _.each(_.keys(METRIC_LUT), (key) =>
                    updateChart(ccKey, key, res.config.results[ccKey][METRIC_LUT[key]], res)
                );
                updateMosGage(threshold, ccKey, res.config.results[ccKey].avgs.mos, res);
                updateStatusBar(ccKey, test, res);
            });

            // the clock, only go if its not running and the test is in running state
            if (test.status === QUICK_TEST_STATUS.RUNNING && !res.clockRunning) {
                startClock(test, res);
            } else if (test.status === QUICK_TEST_STATUS.COMPLETE) {
                res.clockRunning = false;
            } else if (test.status === QUICK_TEST_STATUS.CANCELED) {
                cancelPolling();
                res.clockRunning = false;
            }

            // error handling
            if (test.status === QUICK_TEST_STATUS.ERROR) {
                cancelPolling();
                res.isError = true;
            }

            if (isUnknownErroredTest(test.status, res.config.results)) {
                alertsActions.createErrorAlert(i18n._(t`Caller could not place call`));
            }

            if (_.isEmpty(res.config.results.callee)) {
                res.config.results.callee = buildEmptyAvgFmt();
            }

            setResults(res);
            return test;
        },
        [alertsActions, updateChartData, updateStatusBar]
    );

    /**
     * Polling Loop, returns the function tick which returns itself if we should keep polling
     * @param test
     * @returns {*}
     */
    const setPolling = useCallback(
        (threshold, test, res) => {
            const tick = () => {
                return getQuickTestById(test._id)
                    .then((latestTest) => aggregateTestResults(threshold, latestTest, res))
                    .then((latestTest) => {
                        if (!mountedRef.current || !isPolling) return Promise.resolve();

                        if (isPolling && shouldPoll(latestTest.status)) {
                            setResults((prev) => ({ ...(prev || {}), testRunning: true }));
                            timerRef.current = setTimeout(tick, pollingIntervalRef.current);
                            return timerRef.current;
                        }

                        // looks like we are done with the test
                        return testDone(latestTest, res);
                    });
            };

            return tick();
        },
        [isPolling, testDone, aggregateTestResults, getQuickTestById]
    );

    /**
     * Helper function to stop polling
     */
    function cancelPolling() {
        setIsPolling(false);
        setResults((prev) => ({ ...prev, testRunning: false }));

        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }

    /**
     * starts the clock when the test is running
     * @param test
     */
    function startClock(test, res) {
        const CLOCK_INTERVAL = 1000; // 1 second
        res.clockRunning = true;

        const { duration } = test.testConfig;
        res.clock = {};
        res.clock.val = 0;

        // If the user opened the page, and the call was already in progress, then our 'callCurrentSeconds' will be wrong and
        //  we'll put the wrong value on the 'callClock' and the wrong width on the status bars.
        if (res.pageIsOpenDuringCall) {
            // GUESStimate how long the call has been going
            // ^^^^^^^^^^^
            try {
                res.clock.val = parseInt((getSystemTime().valueOf() - moment(test.createdAt).valueOf()) / 1000, 10);
            } catch (e) {
                log.error(e);
            }

            // Since we're totally just guessing, since we don't know how long call setup took, I'm going to guess that
            //  call setup took about 20 seconds.  That's about right in my experience.
            if (res.clock.val > 20) {
                res.clock.val -= 20;
            } else {
                res.clock.val = 1;
            }
        }

        // ITP - changed as the interval timer is not accurate across all broswers so derive from system time
        const start = _.now();
        let count = 0;
        const timer = setInterval(() => {
            if (count >= duration) {
                clearInterval(timer);
                return;
            }

            res.clock.val = Math.floor((_.now() - start) / 1000);

            count += 1;
        }, CLOCK_INTERVAL);
    }

    /**
     * Helper function to grab a reference to the caller or callee object
     * @param test
     * @param ccKey
     * @returns {*}
     */
    function getCallerOrCallee(test, ccKey) {
        return _.find(test.agentResults, { agentGuid: test.testConfig[ccKey].guid });
    }

    /**
     * Manually updates teh labels on the gage
     * @param gage
     * @param mos
     */
    function updateMosGage(threshold, gage, mos, res) {
        // there is a bug in JustGage that doesn't update the label
        // so do it manually via the jq

        let label = '';
        if (mos > threshold.degraded) {
            label = i18n._(t`Normal`);
        } else if (mos <= threshold.degraded && mos > threshold.critical) {
            label = i18n._(t`Degraded`);
        } else {
            label = i18n._(t`Critical`);
        }

        // Update the series with the new data.
        res[`${gage}Gauge`].series[0].data = [{ y: mos, status: label }];
    }

    /**
     * helper function to update High chart series data
     * @param ccKey
     * @param key
     * @param data
     */
    function updateChart(ccKey, key, data, res) {
        const seriesData = _.first(res.config.chart[ccKey][key].series).data;

        _.each(_.difference(data, seriesData), (point) => {
            // point data looks like [Date Object, value]
            // dont put undefined values on the chart
            if (!_.isUndefined(_.last(point))) seriesData.push(point);
        });
    }

    /**
     * Pull the call Summary out of the results
     * @param agentResult
     * @returns {*}
     */
    function getCallSummary(agentResult) {
        return _.first(_.filter(agentResult.results, (result) => result.resultType === 'callSummary'));
    }

    /**
     * Format the data
     * @param key
     * @param resultData
     * @returns {*}
     */
    function normalizeFmtData(key, resultData) {
        if (resultData === -1) {
            return EMPTY;
        }

        if (key === 'rx_jitter_curr' || key === 'rx_jitter_avg') {
            resultData *= 1;
        }

        return formatNumber(resultData, METRIC_UNITS[key].places) + (METRIC_UNITS[key].metric || '');
    }

    /**
     * Normalizes data ie converts -1's to 0's
     * @param key
     * @param resultData
     * @returns {*}
     */
    function normalizeData(key, resultData) {
        // -1 means null, but we don't have the charts get funky, so make it undefined,
        //  the formatter will show the right thing.
        if (resultData === -1) {
            resultData = undefined;
        }

        if (key === 'rx_jitter_curr' || key === 'rx_jitter_avg') {
            resultData *= 1;
        }

        return resultData;
    }

    /**
     * Helper function to set letiables for voip Phone Quick Test
     */
    const setVoipPhone = useCallback(
        (test, res) => {
            callerCalleeRef.current = ['caller'];
            res.isVoipPulse = false;
            const directCalling = _.get(test, 'testConfig.caller.voipConfiguration.useDirectCalling', false);
            // only if the test should be running
            if (!directCalling && shouldPoll(test.status)) {
                uiToastActions.createToast(
                    createToastConfig(
                        INFO,
                        i18n._(
                            t`Please wait one moment while the nPoint registers with the call manager and places the call to ${test.testConfig.phone}`
                        )
                    )
                );
            }
        },
        [uiToastActions]
    );

    /**
     * assign test fields to the scope and manipulate url if it doesnt have an http
     * @param test
     * @returns {*}
     */
    const assignTestFields = useCallback(
        (threshold, test, res) => {
            res.bar = {};
            res.MS = MS;
            // CommonService.removeDetailsFromStack('app.auth.crud.quickTests.voipDetails');
            // SidebarService.on(OBSERVER_KEY, NAVIGATION.VISIBILITY_CHANGE_EVENT, reflowCharts);

            res.clockRunning = false;
            res.calleeBarVal = 0;
            res.debug = environment.debug;
            res.testRunning = false;
            res.flowGathered = false;
            res.test = {};
            res.isError = false;
            res.isVoipPulse = true;
            res.pageIsOpenDuringCall = false;

            res.callerGauge = getGaugeChartConfig(threshold, _.get(test, 'testConfig.caller.name'), theme);
            res.calleeGauge = getGaugeChartConfig(threshold, _.get(test, 'testConfig.callee.name'), theme);
            // results.mosSquareColor = _.partial(mosSquareColor, {fair: threshold.critical, good: threshold.degraded});

            // if its a phone test
            if (_.isUndefined(_.get(test, 'testConfig.callee.guid'))) {
                setVoipPhone(test, res);
            }

            if (test.status === QUICK_TEST_STATUS.RUNNING) {
                res.pageIsOpenDuringCall = true;
            }
        },
        [environment.debug, setVoipPhone, theme]
    );

    /**
     * Creates the inital data set if there are results it will create the proxy storage for
     * the data that is coming in
     * @param results
     * @param keys
     * @param config
     * @returns {{caller: {}, callee: {}}}
     */
    const createChartData = useCallback((res, keys, test) => {
        const callerCallee = callerCalleeRef.current;
        const ret = _.reduce(
            callerCallee,
            (acc, val) => {
                acc[val] = { updateTimes: [] };
                acc[val].avgs = _.reduce(
                    _.keys(AVG_METRIC_LUT),
                    (ac, key) => {
                        if (key !== 'mos') {
                            ac[key] = EMPTY;
                        } else {
                            ac[key] = 0;
                        }

                        return ac;
                    },
                    {}
                );

                // A formatted Version...
                acc[val].avgsFmt = _.clone(acc[val].avgs);

                return acc;
            },
            {}
        );

        _.each(keys, (key) => _.each(callerCallee, (ccKey) => (ret[ccKey][key] = [])));

        _.each(res, (agentResult) => {
            // phone will be undefined
            if (agentResult.agentGuid === PHONE) {
                return;
            }

            const ccKey = callerOrCallee(test.testConfig, agentResult.agentGuid);
            _.each(keys, (key) => (ret[ccKey][key] = []));
        });

        // information for SIP messages
        ret.sipMessages = { caller: [], callee: [] };

        return ret;
    }, []);

    /**
     * Creates the high chart configuration and sets up the proxy data set
     * @param test
     * @param config
     * @returns {*}
     */
    const createHighChartConfig = useCallback(
        (test, config) => {
            config.results = createChartData(test.agentResults, _.values(METRIC_LUT), test);

            config.chart = {};
            const callerCallee = callerCalleeRef.current;

            _.each(callerCallee, (ccKey) => {
                config.chart[ccKey] = {};
                _.each(
                    _.keys(METRIC_LUT),
                    (key) => (config.chart[ccKey][key] = getChartOptions(key, undefined, theme))
                );
            });

            return config;
        },
        [createChartData, theme]
    );

    /**
     * populates agents and call managers used in this test
     * @param test
     * @returns {*}
     */
    const createConfiguration = useCallback(
        (test, res) => {
            const config = {};
            const callerCallee = callerCalleeRef.current;

            // This shows the duration times in the cloud version, but we'll leave as create date for now...
            res.createdAtFmt = dateTimeFormatter(test.createdAt);

            _.each(callerCallee, (ccKey) => {
                STATUS_AGENT[ccKey] = _.extend(STATUS_AGENT[ccKey], STATUS_AGENT.both, STATUS_LUT);
                res.bar[ccKey] = { val: 0, status: '' };
            });

            res.config = createHighChartConfig(test, config);
        },
        [createHighChartConfig]
    );

    /**
     * helper function for determining callee or caller based based on guid
     * @param config
     * @param guid
     * @returns {*}
     */
    function callerOrCallee(testConfig, guid) {
        if (guid === testConfig.caller.guid) return 'caller';
        return 'callee';
    }

    /**
     * determines if the test is done and if we should still poll for data
     * @param test
     * @returns {*}
     */
    const determinePolling = useCallback(
        (threshold, test, res) => {
            if (shouldPoll(test.status)) {
                // if the test is longer than 5 min lets turn the polling interval up to 10 seconds
                if (test.testConfig && test.testConfig.duration > 300) {
                    pollingIntervalRef.current = 1000 * 10;
                }
                setIsPolling(true);
                return setPolling(threshold, test, res);
            }
            // the test is either completed or errored out
            return testDone(test, res);
        },
        [setPolling, testDone]
    );

    /**
     * So we can show -- rather than blank when there aren't any results
     */
    function buildEmptyAvgFmt() {
        const emptyAvgFmtObj = { avgsFmt: {}, summary: {} };

        // So we can format things properly
        _.each(_.keys(METRIC_LUT), (key) => {
            emptyAvgFmtObj.avgsFmt[key] = EMPTY;
        });

        // And our empty summary values.
        emptyAvgFmtObj.summary = {
            dialDelay: EMPTY,
            ringDelay: EMPTY,
            dataRate: EMPTY,
            codec: EMPTY,
        };

        return emptyAvgFmtObj;
    }

    /**
     * Handles unknown errors in the UI
     * @param status
     * @param proxyData
     * @returns {boolean}
     */
    function isUnknownErroredTest(status, proxyData) {
        return (
            QUICK_TEST_STATUS.COMPLETE === status &&
            proxyData.caller.updateTimes.length === 0 &&
            proxyData.callee.updateTimes.length === 0
        );
    }

    useEffect(() => {
        let cancelled = false;
        mountedRef.current = true;
        const isAlive = () => !cancelled && mountedRef.current;

        const initialize = async () => {
            try {
                const testTypes = await dataService.get(testTypeUrl, {
                    query: JSON.stringify({ name: VOIP_PULSE_TEST_TYPE }),
                });
                const threshold = _.first(_.get(_.first(testTypes), 'thresholds', []));
                if (!threshold) {
                    throw new Error('No threshold configuration found for voip pulse test type');
                }

                if (!isAlive()) return;

                const test = await getQuickTestById(props.id);
                if (!isAlive()) return;

                const res = {};
                assignTestFields(threshold, test, res);
                createConfiguration(test, res);
                aggregateTestResults(threshold, test, res);

                res.loadingPage = false;

                thresholdRef.current = threshold;
                setResults(res);

                determinePolling(threshold, test, res);
            } catch (error) {
                log.error('Failed to initialize voip test result panel', error);
                if (isAlive()) {
                    setResults((prev) => ({
                        ...prev,
                        loadingPage: false,
                        testRunning: false,
                        showLoading: false,
                        isError: true,
                    }));
                }
                cancelPolling();
            }
        };

        initialize();

        return () => {
            cancelled = true;
            mountedRef.current = false;
            cancelPolling();
        };
    }, [
        aggregateTestResults,
        assignTestFields,
        createConfiguration,
        dataService,
        determinePolling,
        getQuickTestById,
        props.id,
    ]);

    useEffect(() => {
        if (results) setCancelTestDisabled(!results.showLoading || results.loadingPage || results.printing);
    }, [results, setCancelTestDisabled]);

    useEffect(() => {
        if (results && setReRunDisabled) {
            setReRunDisabled(results.showLoading || results.loadingPage || results.printing);
        }
    }, [results, setReRunDisabled]);

    return (
        <>
            <Header results={results} />
            <VoipTestNowResultsChartsAndMetrics results={results} mosColor={mosColor} callFlows={callFlows} />
        </>
    );
});

VoipTestNowTestResultPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 130,
};

export default Panel({
    typeAlias: 'VoipTestResultPanel',
    filters: {
        required: [timeRangeFilterId],
        optional: [businessServiceFilterId, statusFilterId, siteFilterId, testTypeFilterId],
    },
})(VoipTestNowTestResultPanel);
